import { type AnalyticsAppFilter, type AnalyticsSessionRow } from '@carsfinders/contracts';
import { Link } from '@tanstack/react-router';
import { ChevronRight, HelpCircle, Monitor, Smartphone, Tablet, type LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { PanelEmpty, PanelError, PanelLoading } from '../../finance/components/panel-state';
import { t } from '../../../lib/i18n';
import { cn } from '../../clients/utils';
import { useAnalyticsSessionPages, useAnalyticsSessions, type AnalyticsSessionsParams } from '../api';
import {
  deviceFullLabel,
  deviceLabel,
  formatDuration,
  formatKyivClock,
  formatKyivDateTime,
  sessionUserLabel,
  sourceLabel,
  visitorLabel,
} from '../format';

const DEVICE_ICON: Record<AnalyticsSessionRow['device'], LucideIcon> = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
  unknown: HelpCircle,
};

/** Desktop column template: expander+time, who, site, duration, pages, entry, source, device. */
const GRID_WITH_WHO =
  'lg:grid-cols-[120px_minmax(0,1.6fr)_90px_90px_60px_minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.2fr)]';
const GRID_NO_WHO =
  'lg:grid-cols-[120px_90px_90px_60px_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1.2fr)]';

/**
 * The sessions query a feed asks for. With a fixed `userId` (the client's
 * «Відвідування» tab) the filters do not apply — the server includes staff
 * sessions for a named user — so both callers that share it (the tab header
 * and this table) build the SAME key and share one request.
 */
export function sessionsParams(input: {
  from?: string;
  to?: string;
  app: AnalyticsAppFilter;
  userId?: string;
  registeredOnly?: boolean;
  includeStaff?: boolean;
}): AnalyticsSessionsParams {
  return input.userId
    ? { app: 'all', registeredOnly: false, includeStaff: false, userId: input.userId }
    : {
        from: input.from,
        to: input.to,
        app: input.app,
        registeredOnly: input.registeredOnly ?? false,
        includeStaff: input.includeStaff ?? false,
      };
}

export interface SessionsTableProps {
  from?: string;
  to?: string;
  app?: AnalyticsAppFilter;
  /** Fixed to one client: hides the filters and the «Хто» column. */
  userId?: string;
}

/**
 * «Стрічка заходів» — newest first. A row's expander (`aria-expanded`) loads
 * that session's pages on demand; «Показати ще» follows the server cursor.
 * Desktop draws a table grid, mobile the same cells as a card
 * (client-list-item.tsx idiom: one markup, `lg:` switches the geometry).
 */
export function SessionsTable({ from, to, app = 'all', userId }: SessionsTableProps) {
  const [registeredOnly, setRegisteredOnly] = useState(false);
  const [includeStaff, setIncludeStaff] = useState(false);
  const fixedUser = Boolean(userId);

  const query = useAnalyticsSessions(
    sessionsParams({ from, to, app, userId, registeredOnly, includeStaff }),
  );
  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];
  const grid = fixedUser ? GRID_NO_WHO : GRID_WITH_WHO;

  return (
    <div className="flex flex-col gap-3">
      {!fixedUser && (
        <div className="flex flex-wrap items-center gap-4 text-[13px]">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={registeredOnly}
              onChange={(event) => setRegisteredOnly(event.target.checked)}
              className="h-4 w-4 accent-cf-brand"
            />
            {t('admin.analytics.sessions.registeredOnly')}
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeStaff}
              onChange={(event) => setIncludeStaff(event.target.checked)}
              className="h-4 w-4 accent-cf-brand"
            />
            {t('admin.analytics.sessions.includeStaff')}
          </label>
        </div>
      )}

      {query.isLoading ? (
        <PanelLoading />
      ) : query.isError ? (
        <PanelError error={query.error} fallbackKey="admin.analytics.failed" />
      ) : rows.length === 0 ? (
        <PanelEmpty messageKey="admin.analytics.sessions.empty" />
      ) : (
        <div className="overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface lg:overflow-x-auto">
          <div
            className={cn(
              'hidden gap-3 border-b border-cf-line-soft px-[18px] py-2.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:grid lg:min-w-[960px]',
              grid,
            )}
          >
            <span>{t('admin.analytics.sessions.col.time')}</span>
            {!fixedUser && <span>{t('admin.analytics.sessions.col.who')}</span>}
            <span>{t('admin.analytics.sessions.col.site')}</span>
            <span>{t('admin.analytics.sessions.col.duration')}</span>
            <span>{t('admin.analytics.sessions.col.pages')}</span>
            <span>{t('admin.analytics.sessions.col.entry')}</span>
            <span>{t('admin.analytics.sessions.col.source')}</span>
            <span>{t('admin.analytics.sessions.col.device')}</span>
          </div>
          <ul>
            {rows.map((row) => (
              <SessionRow key={row.id} row={row} showWho={!fixedUser} grid={grid} />
            ))}
          </ul>
        </div>
      )}

      {query.hasNextPage && (
        <button
          type="button"
          onClick={() => void query.fetchNextPage()}
          disabled={query.isFetchingNextPage}
          className="h-10 self-center rounded-cf-md border border-cf-line bg-cf-surface px-4 text-[13px] font-semibold hover:border-cf-ink disabled:opacity-50"
        >
          {query.isFetchingNextPage ? t('common.loading.default') : t('admin.analytics.sessions.more')}
        </button>
      )}
    </div>
  );
}

function SessionRow({ row, showWho, grid }: { row: AnalyticsSessionRow; showWho: boolean; grid: string }) {
  const [expanded, setExpanded] = useState(false);
  const DeviceIcon = DEVICE_ICON[row.device];
  const pagesId = `session-pages-${row.id}`;

  return (
    <li className="border-b border-cf-line-soft last:border-b-0" data-testid="session-row">
      <div
        className={cn(
          'grid grid-cols-2 gap-x-3 gap-y-1.5 px-3.5 py-3 text-[13px] lg:min-w-[960px] lg:items-center lg:gap-3 lg:px-[18px] lg:py-2.5',
          grid,
        )}
      >
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={pagesId}
          onClick={() => setExpanded((open) => !open)}
          className="col-span-2 inline-flex items-center gap-1.5 text-left font-mono text-[12px] text-cf-ink-muted hover:text-cf-ink lg:col-span-1"
        >
          <ChevronRight
            size={14}
            strokeWidth={1.75}
            className={cn('shrink-0 transition-transform', expanded && 'rotate-90')}
            aria-hidden="true"
          />
          {/* Accessible name = «Сторінки сесії 13.10 14:21»: it starts with the
              visible time's context and contains it (WCAG 2.5.3). */}
          <span className="sr-only">{t('admin.analytics.sessions.expand')} </span>
          {formatKyivDateTime(row.startedAt)}
        </button>

        {showWho && (
          <div className="col-span-2 flex min-w-0 flex-wrap items-center gap-1.5 lg:col-span-1">
            {row.user ? (
              <Link
                to="/clients/$id"
                params={{ id: row.user.id }}
                search={{ tab: 'visits' }}
                className="truncate font-semibold text-cf-brand hover:underline"
              >
                {sessionUserLabel(row.user)}
              </Link>
            ) : (
              <span className="truncate font-semibold">{visitorLabel(row.visitorId)}</span>
            )}
            {!row.user && row.isNewVisitor && (
              <span className="rounded-cf-sm bg-cf-success-tint px-1.5 py-0.5 font-mono text-[10px] text-cf-success uppercase">
                {t('admin.analytics.sessions.newBadge')}
              </span>
            )}
            {row.isStaff && (
              <span className="rounded-cf-sm bg-cf-fill px-1.5 py-0.5 font-mono text-[10px] text-cf-ink-muted uppercase">
                {t('admin.analytics.sessions.staffBadge')}
              </span>
            )}
          </div>
        )}

        <Cell label={t('admin.analytics.sessions.col.site')}>{t(`admin.analytics.app.${row.app}`)}</Cell>
        <Cell label={t('admin.analytics.sessions.col.duration')} mono>
          {formatDuration(row.durationSec)}
        </Cell>
        <Cell label={t('admin.analytics.sessions.col.pages')} mono>
          {row.pageCount}
        </Cell>
        <Cell label={t('admin.analytics.sessions.col.entry')} mono truncate title={row.entryPath}>
          {row.entryPath}
        </Cell>
        <Cell label={t('admin.analytics.sessions.col.source')} truncate title={sourceLabel(row)}>
          {sourceLabel(row)}
        </Cell>
        <Cell label={t('admin.analytics.sessions.col.device')} truncate title={deviceFullLabel(row)}>
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <DeviceIcon size={14} strokeWidth={1.75} className="shrink-0 text-cf-ink-subtle" aria-hidden="true" />
            <span className="truncate">{deviceLabel(row)}</span>
          </span>
        </Cell>
      </div>

      {expanded && <SessionPages id={row.id} domId={pagesId} />}
    </li>
  );
}

/** One cell: a label above the value on the mobile card, the bare value in the desktop grid. */
function Cell({
  label,
  mono,
  truncate,
  title,
  children,
}: {
  label: string;
  mono?: boolean;
  truncate?: boolean;
  /** The full text, for a value the column may cut off. */
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <span className="block text-[10px] text-cf-ink-subtle lg:hidden">{label}</span>
      <span
        className={cn('block', mono && 'font-mono text-[12px]', truncate && 'truncate')}
        title={title}
      >
        {children}
      </span>
    </div>
  );
}

function SessionPages({ id, domId }: { id: string; domId: string }) {
  const query = useAnalyticsSessionPages(id, true);
  const pages = query.data ?? [];

  return (
    <div id={domId} className="bg-cf-surface-sunken px-3.5 pb-3 lg:px-[18px]" data-testid="session-pages">
      {query.isLoading ? (
        <p className="py-2 text-[12px] text-cf-ink-subtle">{t('common.loading.default')}</p>
      ) : query.isError ? (
        <p role="alert" className="py-2 text-[12px] text-cf-danger">
          {t('admin.analytics.failed')}
        </p>
      ) : pages.length === 0 ? (
        <p className="py-2 text-[12px] text-cf-ink-subtle">{t('admin.analytics.sessions.pagesEmpty')}</p>
      ) : (
        <ol className="flex flex-col gap-1 pt-2">
          {pages.map((page, index) => (
            <li key={`${page.at}-${index}`} className="flex gap-3 font-mono text-[12px]">
              <span className="w-[64px] shrink-0 text-cf-ink-subtle">{formatKyivClock(page.at)}</span>
              <span className="min-w-0 truncate">{page.path}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
